import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import type { VmStatusResponse } from "@/lib/types";

const ACTIVE_STATES = new Set<string>([
  "starting",
  "stopping",
  "suspending",
]);

export function useVmStatus(enabled: boolean): {
  data: VmStatusResponse | null;
  error: { status?: number; message: string } | null;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<VmStatusResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<number | null>(null);

  const fetchNow = useCallback(async () => {
    try {
      const res = await api.status();
      setData(res);
      setError(null);
    } catch (e) {
      const err = e as Error & { status?: number };
      setError({ status: err.status, message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") {
        schedule(5000);
        return;
      }
      await fetchNow();
      if (cancelled) return;
      const sessionStatus = data?.vm.sessionStatus ?? null;
      const isActive = sessionStatus ? ACTIVE_STATES.has(sessionStatus) : false;
      schedule(isActive ? 5000 : 30000);
    };

    const schedule = (ms: number) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(tick, ms);
    };

    void fetchNow().then(() => {
      if (!cancelled) {
        const sessionStatus = data?.vm.sessionStatus ?? null;
        const isActive = sessionStatus ? ACTIVE_STATES.has(sessionStatus) : false;
        schedule(isActive ? 5000 : 30000);
      }
    });

    const onVis = () => {
      if (document.visibilityState === "visible") {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        void tick();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchNow]);

  // Adapt poll interval as data changes.
  useEffect(() => {
    if (!enabled) return;
    const sessionStatus = data?.vm.sessionStatus ?? null;
    const isActive = sessionStatus ? ACTIVE_STATES.has(sessionStatus) : false;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      void fetchNow();
    }, isActive ? 5000 : 30000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [data, enabled, fetchNow]);

  return { data, error, loading, refetch: fetchNow };
}
